/**
 * reviewModel.ts
 *
 * Pure helpers for the Title Search Review mode: turn a recorded document's
 * `extractedFields` into reviewable fields, hold the reviewer's actions apart
 * from the extracted data, and derive the document list rows (class,
 * instrument number, "n of m" fields reviewed, status dot) and the footer
 * progress.
 *
 * No React and no network. Whether a document is reviewed and whether Finalize
 * is enabled are delegated to `titleSearch/review.ts`.
 *
 * Requirements: 8.2, 8.3, 8.4, 8.6, 8.7.
 */

import type { Document } from '../../graphql/types';
import { normalizeBoundingBox, sortInReadingOrder } from '../../components/documentViewerGeometry';
import type { BoundingBox } from '../../components/documentViewerGeometry';
import { CONFIDENCE_THRESHOLD } from '../../components/ui/ConfidencePill';
import {
  acceptAllAboveThreshold,
  isDocumentReviewed,
  isFinalizeEnabled,
} from '../../titleSearch/review';
import type { FieldAction, ReviewableDocument } from '../../titleSearch/review';

export { CONFIDENCE_THRESHOLD };

/** One extracted field, exactly as read. Reviewer choices live in `ReviewActions`. */
export interface ReviewField {
  /** Stable key: `${documentId}:${fieldName}` (suffixed `#n` for repeats). Used in the URL. */
  id: string;
  documentId: string;
  /** Raw field name, for example `grantor_name`. */
  name: string;
  /** Display label, for example `grantor name`. */
  label: string;
  /** The value as extracted. */
  value: string;
  confidence: number;
  /** 1-based absolute PDF page. */
  page: number;
  boundingBox: BoundingBox | null;
  /**
   * The action stored with a finalized review (`ACCEPT` / `EDIT` / `REJECT`),
   * with the edited value for an edit. Absent for a field that was never
   * reviewed, and for reviews finalized before actions were stored.
   */
  stored?: ReviewAction;
}

/** The reviewer's choice for one field. `editedValue` is set only for `edit`. */
export interface ReviewAction {
  action: FieldAction;
  editedValue?: string;
}

/** Reviewer choices by field id. A field without an entry has no action yet. */
export type ReviewActions = Record<string, ReviewAction>;

/** A processed recorded document with its reviewable fields. */
export interface ReviewDocument {
  documentId: string;
  /** The file (PDF) the document was split from. */
  fileId: string;
  /** Document class, for example "Warranty Deed". */
  documentClass: string;
  /** How sure the pipeline is of the document class and split (0 to 1), or null when unknown. */
  confidence: number | null;
  /** Instrument or book/page number read from the document, or ''. */
  instrumentNumber: string;
  pageRange: string;
  /** First page of the document in the PDF (1-based). */
  firstPage: number;
  sourceFilename: string;
  errorMessage?: string;
  fields: ReviewField[];
}

export type DocumentReviewStatus = 'reviewed' | 'attention' | 'in-progress' | 'not-started';

// ── Parsing ─────────────────────────────────────────────────────────────────

/** Map a stored `ACCEPT` / `EDIT` / `REJECT` (any case) to a field action. */
function parseStoredAction(raw: unknown): FieldAction | null {
  if (typeof raw !== 'string') return null;
  switch (raw.trim().toUpperCase()) {
    case 'ACCEPT':
      return 'accept';
    case 'EDIT':
      return 'edit';
    case 'REJECT':
      return 'reject';
    default:
      return null;
  }
}

/**
 * The reviewer's actions as stored with a finalized review, by field id. Fields
 * without a stored action have no entry.
 */
export function storedActionsOf(documents: ReadonlyArray<ReviewDocument>): ReviewActions {
  const actions: ReviewActions = {};
  for (const doc of documents) {
    for (const f of doc.fields) {
      if (f.stored) actions[f.id] = f.stored;
    }
  }
  return actions;
}

/** Parse the `extractedFields` JSON into reviewable fields (tolerant of shapes). */
export function parseReviewFields(
  extractedFields: string | null | undefined,
  documentId: string
): ReviewField[] {
  if (!extractedFields) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(extractedFields);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];

  const seen = new Map<string, number>();
  const result: ReviewField[] = [];
  for (const entry of parsed) {
    if (!entry || typeof entry !== 'object') continue;
    const raw = entry as Record<string, unknown>;
    const name = String(raw.field_name ?? raw.field ?? '');
    const valueRaw = raw.field_value ?? raw.value ?? '';
    const confidenceRaw = Number(raw.confidence_score ?? raw.confidence ?? 0);
    const pageRaw = Number(raw.page ?? 1);

    const count = seen.get(name) ?? 0;
    seen.set(name, count + 1);
    const baseId = `${documentId}:${name}`;

    // A finalized review stores the action next to the field. For an edit the
    // stored value is the edited one and `extracted_value` is the original.
    const storedAction = parseStoredAction(raw.action);
    const storedValue = valueRaw == null ? '' : String(valueRaw);
    let value = storedValue;
    let stored: ReviewAction | undefined;
    if (storedAction) {
      if (storedAction === 'edit') {
        stored = { action: 'edit', editedValue: storedValue };
        if (raw.extracted_value != null) value = String(raw.extracted_value);
      } else if (
        storedAction === 'accept' &&
        raw.extracted_value != null &&
        String(raw.extracted_value) !== storedValue
      ) {
        // An edit is stored as ACCEPT with the corrected value and the original
        // in `extracted_value`.
        stored = { action: 'accept', editedValue: storedValue };
        value = String(raw.extracted_value);
      } else {
        stored = { action: storedAction };
      }
    }

    const field: ReviewField = {
      id: count === 0 ? baseId : `${baseId}#${count + 1}`,
      documentId,
      name,
      label: name.replace(/_/g, ' '),
      value,
      confidence: Number.isFinite(confidenceRaw) ? confidenceRaw : 0,
      page: Number.isFinite(pageRaw) && pageRaw >= 1 ? Math.round(pageRaw) : 1,
      boundingBox: normalizeBoundingBox(raw.bounding_box ?? raw.boundingBox ?? null),
    };
    if (stored) field.stored = stored;
    result.push(field);
  }
  // Ids are assigned above, so ordering the list by position leaves them stable.
  return sortInReadingOrder(result, (f) => f);
}

/** First page of a `"3-5"` style range; 1 when it cannot be read. */
export function firstPageOf(pageRange: string | null | undefined): number {
  const n = parseInt((pageRange ?? '').split('-')[0] || '1', 10);
  return Number.isFinite(n) && n >= 1 ? n : 1;
}

const INSTRUMENT_FIELD = /instrument|book[\s_-]*(and[\s_-]*)?page/i;

/** Instrument (or book/page) number among the fields, or ''. */
export function findInstrumentNumber(fields: ReadonlyArray<ReviewField>): string {
  for (const f of fields) {
    if (!INSTRUMENT_FIELD.test(f.name) || f.stored?.action === 'reject') continue;
    const value = (f.stored?.editedValue ?? f.value).trim();
    if (value !== '') return value;
  }
  return '';
}

/**
 * Documents in the order they appear in the PDF: by page within a file. Files
 * keep the order in which their first document was seen, so a document pushed
 * later by the subscription lands in its place and does not reshuffle files.
 * Documents that start on the same page keep their incoming order.
 */
export function sortReviewDocuments(documents: ReadonlyArray<ReviewDocument>): ReviewDocument[] {
  const fileOrder = new Map<string, number>();
  for (const d of documents) if (!fileOrder.has(d.fileId)) fileOrder.set(d.fileId, fileOrder.size);
  return documents
    .map((doc, index) => ({ doc, index }))
    .sort(
      (a, b) =>
        fileOrder.get(a.doc.fileId)! - fileOrder.get(b.doc.fileId)! ||
        a.doc.firstPage - b.doc.firstPage ||
        a.index - b.index
    )
    .map((e) => e.doc);
}

/** Build the reviewable form of a stored document. */
export function toReviewDocument(doc: Document): ReviewDocument {
  const fields = parseReviewFields(doc.extractedFields, doc.documentId);
  return {
    documentId: doc.documentId,
    fileId: doc.fileId,
    documentClass: doc.documentType || 'Document',
    confidence: Number.isFinite(doc.confidenceScore) ? doc.confidenceScore : null,
    instrumentNumber: findInstrumentNumber(fields),
    pageRange: doc.pageRange || '',
    firstPage: firstPageOf(doc.pageRange),
    sourceFilename: doc.sourceFilename || '',
    errorMessage: doc.errorMessage || undefined,
    fields,
  };
}

// ── Field state ─────────────────────────────────────────────────────────────

/** True when the value was read below the threshold (or without a score). */
export function isBelowThreshold(
  field: Pick<ReviewField, 'confidence'>,
  threshold = CONFIDENCE_THRESHOLD
): boolean {
  return !(field.confidence >= threshold);
}

/** The value to show: the edited value for an edited field, else the extracted one. */
export function displayValue(field: ReviewField, actions: ReviewActions): string {
  const entry = actions[field.id];
  // An edit is accepted with its corrected value, so `accept` can carry one too.
  return (entry?.action === 'edit' || entry?.action === 'accept') && entry.editedValue !== undefined
    ? entry.editedValue
    : field.value;
}

/**
 * Return `actions` with one field's action set. Accept and reject drop any
 * edit, unless `accept` is given the corrected value (an edited field is
 * accepted with it).
 */
export function setFieldAction(
  actions: ReviewActions,
  fieldId: string,
  action: FieldAction,
  editedValue?: string
): ReviewActions {
  const entry: ReviewAction =
    action === 'edit' || (action === 'accept' && editedValue !== undefined)
      ? { action, editedValue: editedValue ?? '' }
      : { action };
  return { ...actions, [fieldId]: entry };
}

/**
 * "Accept all ≥ 80%" over `fields`: sets `accept` on every field at or above
 * the threshold that has no action. Fields with an action (and their edited
 * values) and fields below the threshold are left exactly as they were.
 * Returns the same `actions` object when nothing changes.
 */
export function acceptAllFields(
  fields: ReadonlyArray<Pick<ReviewField, 'id' | 'confidence'>>,
  actions: ReviewActions,
  threshold = CONFIDENCE_THRESHOLD
): ReviewActions {
  const current: Record<string, FieldAction | undefined> = {};
  for (const [id, entry] of Object.entries(actions)) current[id] = entry.action;
  const next = acceptAllAboveThreshold(fields, current, threshold);

  let result = actions;
  for (const field of fields) {
    if (actions[field.id] === undefined && next[field.id] === 'accept') {
      if (result === actions) result = { ...actions };
      result[field.id] = { action: 'accept' };
    }
  }
  return result;
}

/** How many of `fields` "Accept all ≥ 80%" would accept right now. */
export function countBulkAcceptable(
  fields: ReadonlyArray<Pick<ReviewField, 'id' | 'confidence'>>,
  actions: ReviewActions,
  threshold = CONFIDENCE_THRESHOLD
): number {
  return fields.filter((f) => actions[f.id] === undefined && f.confidence >= threshold).length;
}

/**
 * "Accept all" over `fields`: sets `accept` on every field that has no action,
 * whatever its confidence. Fields with an action (and their edited values)
 * are left exactly as they were. Returns the same `actions` object when
 * nothing changes.
 */
export function acceptEveryField(
  fields: ReadonlyArray<Pick<ReviewField, 'id'>>,
  actions: ReviewActions
): ReviewActions {
  let result = actions;
  for (const field of fields) {
    if (actions[field.id] === undefined) {
      if (result === actions) result = { ...actions };
      result[field.id] = { action: 'accept' };
    }
  }
  return result;
}

/** How many of `fields` "Accept all" would accept right now. */
export function countWithoutAction(
  fields: ReadonlyArray<Pick<ReviewField, 'id'>>,
  actions: ReviewActions
): number {
  return fields.filter((f) => actions[f.id] === undefined).length;
}

// ── Document and footer state ───────────────────────────────────────────────

/** Minimal shape the review predicates inspect, given the current actions. */
export function toReviewable(doc: ReviewDocument, actions: ReviewActions): ReviewableDocument {
  return { fields: doc.fields.map((f) => ({ action: actions[f.id]?.action })) };
}

/** "n of m" fields that have an action. */
export function documentProgress(
  doc: ReviewDocument,
  actions: ReviewActions
): { reviewed: number; total: number } {
  const reviewed = doc.fields.filter((f) => actions[f.id] !== undefined).length;
  return { reviewed, total: doc.fields.length };
}

/**
 * Status dot for the document list:
 * - reviewed: every field has an action;
 * - attention: not reviewed, and a field below the threshold has no action;
 * - in-progress: some actions, nothing below the threshold waiting;
 * - not-started: no actions yet.
 */
export function documentReviewStatus(
  doc: ReviewDocument,
  actions: ReviewActions,
  threshold = CONFIDENCE_THRESHOLD
): DocumentReviewStatus {
  if (isDocumentReviewed(toReviewable(doc, actions))) return 'reviewed';
  const waitingLow = doc.fields.some(
    (f) => actions[f.id] === undefined && isBelowThreshold(f, threshold)
  );
  if (waitingLow) return 'attention';
  const { reviewed } = documentProgress(doc, actions);
  return reviewed > 0 ? 'in-progress' : 'not-started';
}

export interface ReviewSummary {
  reviewedDocuments: number;
  totalDocuments: number;
  /** Finalize is enabled only with at least one document and all reviewed. */
  finalizeEnabled: boolean;
}

export function summarizeReview(
  documents: ReadonlyArray<ReviewDocument>,
  actions: ReviewActions
): ReviewSummary {
  const reviewable = documents.map((d) => toReviewable(d, actions));
  return {
    reviewedDocuments: reviewable.filter(isDocumentReviewed).length,
    totalDocuments: documents.length,
    finalizeEnabled: documents.length > 0 && isFinalizeEnabled(reviewable),
  };
}
